import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { camelizeKeys } from 'humps';

import { TicketTypeProps, TicketTypesResponse } from './types';
import { IdParamsType } from 'utils/types';
import { getData } from 'services/api';
import { deleteTicket } from 'features/PunchupPages/TicketDetails/services';
import { queryClient } from 'index';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';

export const useTicketTypesApi = () => {
	const { id } = useParams<IdParamsType>();

	const { data: ticketTypes, isFetching } = useQuery({
		queryKey: ['ticketTypes', id],
		queryFn: (): Promise<TicketTypesResponse[]> => getData(`/public/ticketing/ticket_types?event_id=${id}`),
		select: (data) => camelizeKeys(data) as unknown as TicketTypeProps[],
		enabled: !!id,
	});

	return { ticketTypes, isFetching };
};

export const useDeleteTicket = () => {
	const { id: eventId } = useParams<IdParamsType>();
	const { t } = useTranslation();
	const { showNotification, TYPES } = useToastNotification();

	const { mutateAsync } = useMutation(deleteTicket, {
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['ticketTypes', eventId] });
			queryClient.invalidateQueries({ queryKey: ['event', eventId] });
		},
		onError: () => {
			showNotification({
				message: t('ticketDetails-delete-error'),
				type: TYPES.ERROR,
			});
		},
	});

	return { deleteTicket: mutateAsync };
};
